import { useState, useEffect, useCallback, useMemo } from 'react';
import { Car, Plus, Calendar, CheckCircle, XCircle, Edit2, Trash2, X, AlertTriangle } from 'lucide-react';
import { API_URL } from '../config';

const TYPES_VEHICULE = ['Voiture', 'Utilitaire', 'Camion', 'Moto', 'Autre'];
const STATUTS_VEHICULE = ['Disponible', 'En maintenance', 'Hors service'];

const statutResaColor = {
  'En attente': 'bg-yellow-50 dark:bg-yellow-950/40 text-yellow-700 dark:text-yellow-300 border-yellow-200 dark:border-yellow-800',
  'Approuvée': 'bg-green-50 dark:bg-green-950/40 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800',
  'Refusée': 'bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800',
  'Terminée': 'bg-elevated-1 text-text-secondary border-border-subtle',
};

const fmtDate = (d) => new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' });
const fmtDateShort = (d) => new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });

function overlaps(aStart, aEnd, bStart, bEnd) {
  return new Date(aStart) < new Date(bEnd) && new Date(aEnd) > new Date(bStart);
}

export default function Vehicules({ token, user }) {
  const [vehicules, setVehicules] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [occupations, setOccupations] = useState([]);
  const [activeTab, setActiveTab] = useState('vehicules');
  const [showVehiculeForm, setShowVehiculeForm] = useState(false);
  const [showResaForm, setShowResaForm] = useState(false);
  const [editingVehicule, setEditingVehicule] = useState(null);
  const [resaForm, setResaForm] = useState({ vehicule_id: '', date_debut: '', date_fin: '', destination: '', motif: '' });
  const [vForm, setVForm] = useState({
    nom: '', type: 'Voiture', immatriculation: '', capacite: 5,
    statut: 'Disponible', kilometrage: 0, consommation: 0,
    date_controle_technique: '', date_controle_pollution: ''
  });
  const [loading, setLoading] = useState(false);

  const canManageVehicules = ['Patron', 'Super Manager'].includes(user?.role_name);

  const fetchVehicules = useCallback(async () => {
    const res = await fetch(`${API_URL}/vehicules`, { headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) setVehicules(await res.json());
  }, [token]);

  const fetchReservations = useCallback(async () => {
    const res = await fetch(`${API_URL}/reservations-vehicules`, { headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) setReservations(await res.json());
  }, [token]);

  const fetchOccupations = useCallback(async () => {
    const res = await fetch(`${API_URL}/vehicules/occupations`, { headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) setOccupations(await res.json());
  }, [token]);

  useEffect(() => {
    fetchVehicules();
    fetchReservations();
    fetchOccupations();
  }, [fetchVehicules, fetchReservations, fetchOccupations]);

  // Occupations par véhicule (triées par date, filtrées dans le futur)
  const occupationsByVehicule = useMemo(() => {
    const map = {};
    const now = new Date();
    occupations.forEach(o => {
      if (new Date(o.date_fin) < now) return;
      if (!map[o.vehicule_id]) map[o.vehicule_id] = [];
      map[o.vehicule_id].push(o);
    });
    return map;
  }, [occupations]);

  // Conflits pour le formulaire de réservation (basés sur les dates saisies)
  const resaConflicts = useMemo(() => {
    if (!resaForm.date_debut || !resaForm.date_fin) return {};
    const conflicts = {};
    occupations.forEach(o => {
      if (overlaps(resaForm.date_debut, resaForm.date_fin, o.date_debut, o.date_fin)) {
        if (!conflicts[o.vehicule_id]) conflicts[o.vehicule_id] = [];
        conflicts[o.vehicule_id].push(o);
      }
    });
    return conflicts;
  }, [occupations, resaForm.date_debut, resaForm.date_fin]);

  const submitVehicule = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const method = editingVehicule ? 'PUT' : 'POST';
      const url = editingVehicule ? `${API_URL}/vehicules/${editingVehicule.id}` : `${API_URL}/vehicules`;
      const response = await fetch(url, {
        method,
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(vForm)
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erreur lors de la sauvegarde');
      }
      const result = await response.json();
      alert(result.message || (editingVehicule ? 'Véhicule mis à jour' : 'Véhicule créé'));
      setShowVehiculeForm(false);
      setEditingVehicule(null);
      setVForm({ nom: '', type: 'Voiture', immatriculation: '', capacite: 5, statut: 'Disponible', kilometrage: 0, consommation: 0, date_controle_technique: '', date_controle_pollution: '' });
      fetchVehicules();
    } catch (error) {
      alert(error.message || 'Erreur lors de la sauvegarde du véhicule');
    } finally {
      setLoading(false);
    }
  };

  const deleteVehicule = async (id) => {
    if (!window.confirm('Supprimer ce véhicule ?')) return;
    await fetch(`${API_URL}/vehicules/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    fetchVehicules();
  };

  const editVehicule = (v) => {
    setEditingVehicule(v);
    setVForm({
      nom: v.nom, type: v.type, immatriculation: v.immatriculation,
      capacite: v.capacite, statut: v.statut, kilometrage: v.kilometrage,
      consommation: v.consommation, date_controle_technique: v.date_controle_technique || '',
      date_controle_pollution: v.date_controle_pollution || ''
    });
    setShowVehiculeForm(true);
  };

  const submitReservation = async (e) => {
    e.preventDefault();
    // Avertir si conflit mais laisser valider
    const conflicts = resaConflicts[resaForm.vehicule_id];
    if (conflicts && conflicts.length > 0) {
      const detail = conflicts.map(c => `• ${c.type === 'evenement' ? '📅' : '🔑'} ${c.label} (${fmtDate(c.date_debut)} → ${fmtDate(c.date_fin)})`).join('\n');
      const ok = window.confirm(`⚠️ Ce véhicule est déjà occupé sur cette période :\n\n${detail}\n\nConfirmer quand même la réservation ?`);
      if (!ok) return;
    }
    setLoading(true);
    const res = await fetch(`${API_URL}/reservations-vehicules`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(resaForm)
    });
    if (res.ok) {
      setShowResaForm(false);
      setResaForm({ vehicule_id: '', date_debut: '', date_fin: '', destination: '', motif: '' });
      fetchReservations();
      fetchOccupations();
    } else {
      const err = await res.json();
      alert(err.error || 'Erreur lors de la réservation');
    }
    setLoading(false);
  };

  const updateResaStatut = async (id, statut) => {
    await fetch(`${API_URL}/reservations-vehicules/${id}`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ statut })
    });
    fetchReservations();
    fetchOccupations();
  };

  return (
    <div className="px-3 sm:p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-text-primary">Véhicules</h1>
          <p className="text-text-muted text-sm mt-1">Gestion du parc automobile et réservations</p>
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          <button
            onClick={() => setShowResaForm(true)}
            className="flex-1 sm:flex-initial flex items-center justify-center gap-2 bg-green-600 text-white px-4 py-2.5 rounded-lg hover:bg-green-700 transition-colors text-sm"
          >
            <Calendar size={16} />
            Réserver
          </button>
          {canManageVehicules && (
            <button
              onClick={() => { setShowVehiculeForm(true); setEditingVehicule(null); setVForm({ nom: '', type: 'Voiture', immatriculation: '', capacite: 5, statut: 'Disponible', kilometrage: 0, consommation: 0, date_controle_technique: '', date_controle_pollution: '' }); }}
              className="flex-1 sm:flex-initial flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-2.5 rounded-lg hover:bg-blue-700 transition-colors text-sm"
            >
              <Plus size={16} />
              Ajouter
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-elevated-1 rounded-xl p-1 mb-6 w-fit">
        {[{ key: 'vehicules', label: 'Parc véhicules' }, { key: 'reservations', label: 'Réservations' }].map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === tab.key ? 'bg-surface text-blue-700 dark:text-blue-300 shadow-sm' : 'text-text-secondary hover:text-text-primary'}`}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Formulaire véhicule */}
      {showVehiculeForm && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
          <div className="bg-surface rounded-xl w-full max-w-lg p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-text-primary">{editingVehicule ? 'Modifier' : 'Ajouter un véhicule'}</h2>
              <button onClick={() => setShowVehiculeForm(false)}><X size={18} className="text-text-muted" /></button>
            </div>
            <form onSubmit={submitVehicule} className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-text-secondary mb-1">Nom *</label>
                <input required className="w-full border rounded-lg px-3 py-2 text-sm"
                  value={vForm.nom} onChange={e => setVForm({ ...vForm, nom: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1">Immatriculation *</label>
                <input required className="w-full border rounded-lg px-3 py-2 text-sm"
                  value={vForm.immatriculation} onChange={e => setVForm({ ...vForm, immatriculation: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1">Type</label>
                <select className="w-full border rounded-lg px-3 py-2 text-sm"
                  value={vForm.type} onChange={e => setVForm({ ...vForm, type: e.target.value })}>
                  {TYPES_VEHICULE.map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1">Statut</label>
                <select className="w-full border rounded-lg px-3 py-2 text-sm"
                  value={vForm.statut} onChange={e => setVForm({ ...vForm, statut: e.target.value })}>
                  {STATUTS_VEHICULE.map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1">Capacité</label>
                <input type="number" min={0} className="w-full border rounded-lg px-3 py-2 text-sm"
                  value={vForm.capacite} onChange={e => setVForm({ ...vForm, capacite: parseInt(e.target.value) || 0 })} />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1">Kilométrage</label>
                <input type="number" min={0} className="w-full border rounded-lg px-3 py-2 text-sm"
                  value={vForm.kilometrage} onChange={e => setVForm({ ...vForm, kilometrage: parseInt(e.target.value) })} />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1">🔧 Contrôle technique</label>
                <input type="date" className="w-full border rounded-lg px-3 py-2 text-sm"
                  value={vForm.date_controle_technique} onChange={e => setVForm({ ...vForm, date_controle_technique: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1">🌿 Contrôle pollution</label>
                <input type="date" className="w-full border rounded-lg px-3 py-2 text-sm"
                  value={vForm.date_controle_pollution} onChange={e => setVForm({ ...vForm, date_controle_pollution: e.target.value })} />
              </div>
              <div className="col-span-2 flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowVehiculeForm(false)}
                  className="px-4 py-2 text-sm border border-border-strong rounded-lg hover:bg-elevated-1">Annuler</button>
                <button type="submit" disabled={loading}
                  className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                  {editingVehicule ? 'Enregistrer' : 'Ajouter'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Formulaire réservation */}
      {showResaForm && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
          <div className="bg-surface rounded-xl w-full max-w-lg p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-text-primary">Réserver un véhicule</h2>
              <button onClick={() => setShowResaForm(false)}><X size={18} className="text-text-muted" /></button>
            </div>
            <form onSubmit={submitReservation} className="space-y-4">
              {/* Dates en premier pour activer la détection de conflits */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-1">Date début *</label>
                  <input type="datetime-local" required className="w-full border rounded-lg px-3 py-2 text-sm"
                    value={resaForm.date_debut} onChange={e => setResaForm({ ...resaForm, date_debut: e.target.value })} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-1">Date fin *</label>
                  <input type="datetime-local" required className="w-full border rounded-lg px-3 py-2 text-sm"
                    value={resaForm.date_fin} onChange={e => setResaForm({ ...resaForm, date_fin: e.target.value })} />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1">Véhicule *</label>
                <select required className="w-full border rounded-lg px-3 py-2 text-sm"
                  value={resaForm.vehicule_id} onChange={e => setResaForm({ ...resaForm, vehicule_id: e.target.value })}>
                  <option value="">Sélectionner un véhicule</option>
                  {vehicules.filter(v => v.statut === 'Disponible').map(v => {
                    const conflicts = (resaForm.date_debut && resaForm.date_fin) ? resaConflicts[v.id] : null;
                    const hasConflict = conflicts && conflicts.length > 0;
                    return (
                      <option key={v.id} value={v.id}>
                        {hasConflict ? '⚠️ ' : '✅ '}{v.nom} — {v.immatriculation}
                        {hasConflict ? ` (occupé: ${fmtDate(conflicts[0].date_debut)}→${fmtDate(conflicts[0].date_fin)})` : ''}
                      </option>
                    );
                  })}
                </select>

                {/* Alerte conflit pour le véhicule sélectionné */}
                {resaForm.vehicule_id && resaForm.date_debut && resaForm.date_fin && resaConflicts[resaForm.vehicule_id] && (
                  <div className="mt-2 p-3 bg-orange-50 dark:bg-orange-950/40 border border-orange-200 rounded-lg">
                    <div className="flex items-center gap-2 text-orange-700 dark:text-orange-300 font-medium text-sm mb-1">
                      <AlertTriangle size={14} />
                      Ce véhicule est déjà occupé sur cette période
                    </div>
                    {resaConflicts[resaForm.vehicule_id].map((c, i) => (
                      <div key={i} className="text-xs text-orange-600 dark:text-orange-400 flex items-center gap-1 mt-0.5">
                        <span>{c.type === 'evenement' ? '📅' : '🔑'}</span>
                        <span className="font-medium">{c.label}</span>
                        <span className="text-orange-400">({fmtDate(c.date_debut)} → {fmtDate(c.date_fin)})</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1">Destination</label>
                <input className="w-full border rounded-lg px-3 py-2 text-sm"
                  value={resaForm.destination} onChange={e => setResaForm({ ...resaForm, destination: e.target.value })} />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowResaForm(false)}
                  className="px-4 py-2 text-sm border border-border-strong rounded-lg hover:bg-elevated-1">Annuler</button>
                <button type="submit" disabled={loading}
                  className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50">Réserver</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Contenu onglet Parc véhicules */}
      {activeTab === 'vehicules' ? (
        vehicules.length === 0 ? (
          <div className="text-center py-16 text-text-muted">
            <Car size={48} className="mx-auto mb-3 opacity-30" />
            <p>Aucun véhicule enregistré</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {vehicules.map(v => {
              const occs = occupationsByVehicule[v.id] || [];
              const nextOccs = occs.slice(0, 3);
              const isCurrentlyOccupied = occs.some(o => new Date(o.date_debut) <= new Date() && new Date(o.date_fin) >= new Date());

              return (
                <div key={v.id} className={`bg-surface rounded-xl border p-4 shadow-sm ${isCurrentlyOccupied ? 'border-orange-300 dark:border-orange-700' : 'border-border-subtle'}`}>
                  {/* En-tête */}
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="font-semibold text-text-primary">{v.nom}</h3>
                      <p className="text-sm text-text-muted">{v.immatriculation} · {v.type}</p>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      isCurrentlyOccupied ? 'bg-orange-100 dark:bg-orange-900/40 text-orange-700' :
                      v.statut === 'Disponible' ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300' :
                      v.statut === 'En maintenance' ? 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300' :
                      'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300'
                    }`}>
                      {isCurrentlyOccupied ? 'En utilisation' : v.statut}
                    </span>
                  </div>

                  {/* Infos */}
                  <div className="text-sm text-text-secondary mb-3">
                    {v.capacite} place{v.capacite !== 1 ? 's' : ''} · {v.kilometrage?.toLocaleString('fr-FR')} km
                  </div>

                  {/* Occupations à venir */}
                  {nextOccs.length > 0 && (
                    <div className="mb-3">
                      <p className="text-xs font-medium text-text-muted uppercase tracking-wide mb-1.5">Occupations à venir</p>
                      <div className="space-y-1">
                        {nextOccs.map((o, i) => (
                          <div key={i} className={`flex items-center gap-1.5 text-xs px-2 py-1 rounded-md ${
                            o.type === 'evenement' ? 'bg-purple-50 dark:bg-purple-950/40 text-purple-700 dark:text-purple-300' : 'bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300'
                          }`}>
                            <span>{o.type === 'evenement' ? '📅' : '🔑'}</span>
                            <span className="font-medium truncate flex-1">{o.label}</span>
                            <span className="text-text-muted whitespace-nowrap">{fmtDateShort(o.date_debut)} → {fmtDateShort(o.date_fin)}</span>
                          </div>
                        ))}
                        {occs.length > 3 && (
                          <p className="text-xs text-text-muted pl-1">+{occs.length - 3} autre(s)…</p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  {canManageVehicules && (
                    <div className="flex gap-3 pt-3 border-t border-border-subtle">
                      <button onClick={() => editVehicule(v)} className="flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:text-blue-200">
                        <Edit2 size={12} /> Modifier
                      </button>
                      <button onClick={() => deleteVehicule(v.id)} className="flex items-center gap-1 text-xs text-red-500 hover:text-red-700 dark:text-red-300 ml-auto">
                        <Trash2 size={12} /> Supprimer
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )
      ) : (
        /* Onglet Réservations */
        reservations.length === 0 ? (
          <div className="text-center py-16 text-text-muted">
            <Calendar size={48} className="mx-auto mb-3 opacity-30" />
            <p>Aucune réservation</p>
          </div>
        ) : (
          <div className="space-y-3">
            {reservations.map(r => (
              <div key={r.id} className="bg-surface rounded-xl border border-border-subtle p-4 shadow-sm flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <span className="font-semibold text-text-primary text-sm sm:text-base">{r.vehicule_nom}</span>
                    <span className="text-xs text-text-muted">{r.immatriculation}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${statutResaColor[r.statut] || 'bg-elevated-1 text-text-secondary border-border-subtle'}`}>{r.statut}</span>
                  </div>
                  <div className="text-sm text-text-secondary">
                    {fmtDate(r.date_debut)} → {fmtDate(r.date_fin)}
                    {r.destination && <span className="ml-3">{r.destination}</span>}
                  </div>
                  <p className="text-xs text-text-muted mt-1">Par {r.employe_nom}</p>
                </div>
                {canManageVehicules && r.statut === 'En attente' && (
                  <div className="flex gap-2 flex-shrink-0">
                    <button onClick={() => updateResaStatut(r.id, 'Approuvée')}
                      className="flex items-center gap-1 text-xs bg-green-100 dark:bg-green-900/40 text-green-700 px-3 py-1.5 rounded-lg hover:bg-green-200 dark:bg-green-800/40">
                      <CheckCircle size={12} /> Approuver
                    </button>
                    <button onClick={() => updateResaStatut(r.id, 'Refusée')}
                      className="flex items-center gap-1 text-xs bg-red-100 dark:bg-red-900/40 text-red-700 px-3 py-1.5 rounded-lg hover:bg-red-200 dark:bg-red-800/40">
                      <XCircle size={12} /> Refuser
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}
